import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from './SetCreation.module.css';

export default function CreateSetPage() {
  const navigate = useNavigate();
  
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [releaseDate, setReleaseDate] = useState("");
  const [rarities, setRarities] = useState([]);
  const [language, setLanguage] = useState("EN");
  const [languages, setLanguages] = useState(["EN", "JP"]); // Default languages
  const [game, setGame] = useState("Pokemon");
  const [games, setGames] = useState(["Pokemon", "Yu-Gi-Oh!", "Magic: The Gathering", "One Piece"]); // Default games
  const [newRarityName, setNewRarityName] = useState("");
  const [error, setError] = useState("");

  // Fetch available languages and games on component mount
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/sets/languages`, {
          method: "GET",
          headers: { "Content-Type": "application/json" }
        });
        
        if (res.ok) {
          const langs = await res.json();
          console.log("Available languages:", langs);
          setLanguages(langs);
        }
      } catch (err) {
        console.error("Failed to fetch languages:", err);
        // Keep default languages if fetch fails
      }
    };

    const fetchGames = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/sets/games`, {
          method: "GET",
          headers: { "Content-Type": "application/json" }
        });
        
        if (res.ok) {
          const gms = await res.json();
          console.log("Available games:", gms);
          setGames(gms);
        }
      } catch (err) {
        console.error("Failed to fetch games:", err);
        // Keep default games if fetch fails
      }
    };

    fetchLanguages();
    fetchGames();
  }, []);

  /* -------------------- Add rarity -------------------- */
  const addRarity = () => {
    if (!newRarityName.trim()) return;
    
    // Check for duplicates
    if (rarities.some(r => r.name.toLowerCase() === newRarityName.toLowerCase())) {
      setError("This rarity already exists");
      return;
    }

    setRarities(prev => [...prev, { name: newRarityName.trim(), hitRate: 0 }]);
    setNewRarityName("");
    setError("");
  };

  /* -------------------- Remove rarity -------------------- */
  const removeRarity = (index) => {
    setRarities(prev => prev.filter((_, idx) => idx !== index));
  };

  /* -------------------- Set creation -------------------- */
  const createSet = async () => {
    setError("");

    // Validation
    if (!name.trim()) {
      setError("Set name is required");
      return;
    }

    if (!code.trim()) {
      setError("Set code is required");
      return;
    }

    if (rarities.length === 0) {
      setError("Please add at least one rarity");
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/sets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          code: code.trim(),
          language: language,
          game: game,
          release: releaseDate || undefined,
          rarities: rarities
        })
      });

      if (res.status === 409) {
        const data = await res.json();
        setError(data.error || "Set code already exists");
        return;
      }

      if (!res.ok) {
        const data = await res.json();
        setError(data.error || "Failed to create set");
        return;
      }

      alert("Set created successfully!");
      navigate("/dashboard");
    } catch (err) {
      setError("Failed to create set. Please try again.");
    }
  };

  /* -------------------- Render -------------------- */
  return (
    <div className={styles.pageContainer}>
      <div className={styles.header}>
        <h1 className={styles.pageTitle}>Create Pokémon Set</h1>
        <button
          onClick={() => navigate("/dashboard")}
          className={styles.backButton}
        >
          ← Back to Dashboard
        </button>
      </div>

      {error && (
        <div className={styles.errorBox}>
          {error}
        </div>
      )}

      {/* Set Name */}
      <div className={styles.formSection}>
        <label className={styles.label}>
          Set Name <span className={styles.required}>*</span>
        </label>
        <input
          type="text"
          className={styles.input}
          placeholder="e.g., Scarlet & Violet"
          value={name}
          onChange={e => setName(e.target.value)}
        />
      </div>

      {/* Set Code */}
      <div className={styles.formSection}>
        <label className={styles.label}>
          Set Code <span className={styles.required}>*</span>
        </label>
        <input
          type="text"
          className={styles.input}
          placeholder="e.g., SVI"
          value={code}
          onChange={e => setCode(e.target.value)}
        />
        <p className={styles.helpText}>
          Set code will be converted to uppercase and must be unique
        </p>
      </div>

      {/* Language */}
      <div className={styles.formSection}>
        <label className={styles.label}>
          Language <span className={styles.required}>*</span>
        </label>
        <select
          className={styles.select}
          value={language}
          onChange={e => setLanguage(e.target.value)}
        >
          {languages.map(lang => (
            <option key={lang} value={lang}>{lang}</option>
          ))}
        </select>
      </div>

      {/* Game */}
      <div className={styles.formSection}>
        <label className={styles.label}>
          Game <span className={styles.required}>*</span>
        </label>
        <select
          className={styles.select}
          value={game}
          onChange={e => setGame(e.target.value)}
        >
          {games.map(gm => (
            <option key={gm} value={gm}>{gm}</option>
          ))}
        </select>
      </div>

      {/* Release Date */}
      <div className={styles.formSection}>
        <label className={styles.label}>Release Date (optional)</label>
        <input
          type="date"
          className={styles.input}
          value={releaseDate}
          onChange={e => setReleaseDate(e.target.value)}
        />
      </div>

      {/* Rarities */}
      <div className={styles.formSection}>
        <label className={styles.label}>
          Rarities <span className={styles.required}>*</span>
        </label>
        
        <div className={styles.rarityInputRow}>
          <input
            type="text"
            className={styles.rarityInput}
            placeholder="e.g., Common, Uncommon, Rare"
            value={newRarityName}
            onChange={e => setNewRarityName(e.target.value)}
            onKeyPress={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                addRarity();
              }
            }}
          />
          <button
            onClick={addRarity}
            className={styles.addRarityButton}
          >
            Add Rarity
          </button>
        </div>

        {/* Rarities List */}
        {rarities.length > 0 && (
          <div className={styles.raritiesList}>
            <p className={styles.raritiesListTitle}>Added Rarities:</p>
            <div className={styles.raritiesContainer}>
              {rarities.map((rarity, idx) => (
                <div
                  key={idx}
                  className={styles.rarityCard}
                >
                  <input
                    type="text"
                    value={rarity.name}
                    onChange={(e) => {
                      const newRarities = [...rarities];
                      newRarities[idx].name = e.target.value;
                      setRarities(newRarities);
                    }}
                    className={styles.rarityNameInput}
                    placeholder="Rarity name"
                  />
                  <div className={styles.rarityHitRateGroup}>
                    <label className={styles.rarityHitRateLabel}>Hit Rate [1/?]:</label>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={rarity.hitRate}
                      onChange={(e) => {
                        const newRarities = [...rarities];
                        newRarities[idx].hitRate = Number(e.target.value);
                        setRarities(newRarities);
                      }}
                      className={styles.rarityHitRateInput}
                      placeholder="0.00"
                    />
                  </div>
                  <button
                    onClick={() => removeRarity(idx)}
                    className={styles.removeButton}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Submit */}
      <div className={styles.submitSection}>
        <button
          onClick={createSet}
          className={styles.submitButton}
        >
          Create Set
        </button>
      </div>
    </div>
  );
}
